import * as React from 'react';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useResponsiveBreakPoints, cn } from '@restworld/utils-common';

import { ModalDesktop } from './desktop/modal-desktop';
import { ModalMobile } from './mobile/modal-mobile';
import { ModalVariant } from './custom-modals';
import { ModalHeader } from './modal-header';
import { ModalContent } from './modal-content';

export interface CustomModalProps {
  children: ReactNode;

  /**
   * used only on desktop for layout modal
   */
  variant?: ModalVariant;

  /**
   *Configure modal layout for desktop
   */
  layout: 'drawer' | 'modal';

  /**
   * Whether is open
   */
  isOpen: boolean;

  /**
   * Drawer props only used ONLY when layout == drawer
   *
   */
  drawerProps?: {
    drawerPosition?: 'left' | 'right';
    drawerStyles?: React.CSSProperties;
    drawerWidth?: number;
  };

  /**
   * Modal props only used ONLY when layout == modal
   */
  modalProps?: {
    modalStyles?: React.CSSProperties;
  };

  /**
   *Configure whether it is controlled component (default false)
   */
  isControlled?: boolean;

  /**
   * Callback when to close it
   */
  onClose: () => void;
  /**
   * halts gesture manually
   */
  haltGestures?: boolean;
}

export function Modal({
  children,
  isOpen,
  layout,
  drawerProps = {},
  modalProps = {},
  isControlled,
  variant,
  onClose,
  haltGestures,
}: CustomModalProps) {
  const { isDesktop } = useResponsiveBreakPoints();
  const [openModal, setOpenModal] = useState<boolean>(isOpen);

  useEffect(() => {
    if (isControlled) {
      setOpenModal(isOpen);
    }
  }, [setOpenModal, isControlled, isOpen]);

  const handleToggleModal = useCallback(() => {
    setOpenModal(!openModal);
  }, [setOpenModal, openModal]);

  return isDesktop ? (
    <ModalDesktop
      variant={variant}
      isOpen={isControlled ? openModal : isOpen}
      layout={layout}
      onClose={isControlled ? handleToggleModal : onClose}
      modalProps={modalProps}
      drawerProps={drawerProps}
    >
      {children}
    </ModalDesktop>
  ) : (
    <ModalMobile
      isOpen={isOpen}
      onClose={isControlled ? handleToggleModal : onClose}
      variant={variant}
      haltGestures={haltGestures}
    >
      {children}
    </ModalMobile>
  );
}

export default Modal;

export const ModalOverlay = ({ show, className, ...props }: any) => (
  <div
    className={cn(
      'fixed overflow-hidden',
      show && 'left-0 top-0 w-full h-full z-50 bg-gray-300 bg-opacity-30',
      className
    )}
    {...props}
  />
);
export { ModalHeader, ModalContent };
